import React from 'react';
import { Link } from 'react-router-dom';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Avatar from "@mui/material/Avatar";
import { alpha } from '@mui/material/styles';
import EventOutlinedIcon from '@mui/icons-material/EventOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';

import FreakActions from './FreakActions';
import { formatDate, formatDateAgo } from '../../utils/dateUtils';
import { DEFAULT_THEME_COLOR } from "../../constants/theme";
import { STATUS_CANCELED, STATUS_FINISHED, STATUS_IN_PROGRESS, STATUS_PLANNED } from '../../constants/commonConstants';
import { getFreakIcon, isFeed } from "../../utils/freakUtils";
import { countGoing, getStatusKey, pluralize } from '../../utils/freakActions';
import useTranslation from "../../customHooks/translations";

const STATUS_COLORS = {
  [STATUS_PLANNED]: '#36a25f',
  [STATUS_IN_PROGRESS]: '#ff8836',
  [STATUS_FINISHED]: '#79DDD0',
  [STATUS_CANCELED]: '#c9364d',
};

const FreakMeta = props => {
  const translation = useTranslation();
  const freak = props.freak;
  const statusKey = getStatusKey(freak.status);
  const statusColor = STATUS_COLORS[freak.status] || DEFAULT_THEME_COLOR;

  return (
    <Box className="article-meta" sx={{ maxWidth: 720 }}>
      <Stack direction="row" spacing={2} alignItems="center">
        {
          freak?.context
            ? <Box
              aria-hidden
              sx={{
                width: 60,
                height: 60,
                flexShrink: 0,
                borderRadius: 4,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                background: `linear-gradient(135deg, ${DEFAULT_THEME_COLOR} 0%, #4facfe 100%)`,
                boxShadow: `0 6px 18px ${alpha(DEFAULT_THEME_COLOR, 0.35)}`,
                '& svg': { fontSize: 32 },
              }}
            >
              {getFreakIcon(freak.context)}
            </Box>
            : null
        }
        <Box sx={{ minWidth: 0 }}>
          {
            props.showTitle !== false
              ? <Typography variant="h4" component="h1" sx={{ fontWeight: 700, wordBreak: 'break-word' }}>
                {freak.title}
              </Typography>
              : null
          }
          <Stack direction="row" spacing={1.5} useFlexGap flexWrap="wrap" alignItems="center" sx={{ mt: 0.75 }}>
            {
              statusKey && !isFeed(freak)
                ? <Box
                  data-testid="freak-status-pill"
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.75,
                    px: 1.25,
                    py: 0.25,
                    borderRadius: 3,
                    fontSize: 12,
                    fontWeight: 700,
                    color: statusColor,
                    backgroundColor: alpha(statusColor, 0.14),
                  }}
                >
                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: statusColor }}/>
                  {translation.freaks.status[statusKey]}
                </Box>
                : null
            }
            {
              freak.startDate && freak.status === STATUS_PLANNED
                ? <Stack direction="row" spacing={0.5} alignItems="center" sx={{ color: 'text.secondary', fontSize: 13 }}>
                  <EventOutlinedIcon sx={{ fontSize: 16 }}/>
                  <span>{formatDate(freak.startDate)}</span>
                </Stack>
                : null
            }
            {
              !isFeed(freak)
                ? <Stack direction="row" spacing={0.5} alignItems="center" sx={{ color: 'text.secondary', fontSize: 13 }}>
                  <GroupsOutlinedIcon sx={{ fontSize: 16 }}/>
                  <span>{pluralize(translation.freakActions.going, countGoing(freak))}</span>
                </Stack>
                : null
            }
          </Stack>
        </Box>
      </Stack>

      <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mt: 2 }}>
        <Link to={`/@${freak.author.username}`}>
          <Avatar alt={freak.author.username} src={freak?.author?.imageUrl}/>
        </Link>
        <Box>
          <Stack direction="row" spacing={0.75} alignItems="center">
            <Link to={`/@${freak.author.username}`} className="author" style={{ color: DEFAULT_THEME_COLOR, fontWeight: 600 }}>
              {freak.author.username}
            </Link>
            <Box
              component="span"
              sx={{
                px: 0.75,
                borderRadius: 1.5,
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: 0.5,
                textTransform: 'uppercase',
                color: DEFAULT_THEME_COLOR,
                backgroundColor: alpha(DEFAULT_THEME_COLOR, 0.14),
              }}
            >
              {translation.freakActions.organizer}
            </Box>
          </Stack>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {formatDateAgo(freak.createdAt)}
          </Typography>
        </Box>
      </Stack>

      <FreakActions freak={freak}/>
    </Box>
  );
};

export default FreakMeta;
